import { DataSource } from '@angular/cdk/collections';
import {
    SelectedPayableFilters
} from '../../components/app-layout/accounts-payable/payables-dashboard/payable-filters/payable-filters.component';
import { Payable } from '../../models/entities/payable';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { HighlightResource } from '../../models/table/highlight-resource';
import { PayablesService } from './payables.service';
import { PaginationLinks, PaginationMeta } from '../../models/entities/pagination';
import { catchError, filter, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { PayableFilterTab } from './payables-data.service';

export interface PayableDatasourceOptions
{
    status?: string[];
    approvalStatus?: string[];
    vendor?: number;
    fulfillment?: string;
    year?: number;
    startDate?: string;
    endDate?: string;
    dueYear?: number;
    dueStartDate?: string;
    dueEndDate?: string;
    payableApprover?: number;
    payableApprovalsCompleted?: boolean;
    overdue?: boolean;
    deleted?: boolean;
    hideCancelled?: boolean;
}

export class PayablesDatasource extends HighlightResource<Payable> implements DataSource<Payable>
{
    private readonly payableSubject$ = new BehaviorSubject<Payable[]>([]);
    private readonly loadingSubject$ = new BehaviorSubject<boolean>(false);
    private readonly countSubject$ = new BehaviorSubject<number>(0);

    private readonly cancelInFlight$ = new Subject<void>();

    constructor(private payablesService: PayablesService) {
        super();
    }

    get count$() {
        return this.countSubject$.asObservable();
    }

    get loading$() {
        return this.loadingSubject$.asObservable();
    }

    set loading(loading: boolean) {
        this.loadingSubject$.next(loading);
    }

    /** Get current payables data synchronously */
    get data(): Payable[] {
        return this.payableSubject$.getValue();
    }

    /**
     * Maps frontend camelCase column names to backend snake_case API column names.
     * The backend expects snake_case column names for sorting.
     */
    private mapSortColumn(column: string): string {
        const columnMapping: { [key: string]: string } = {
            'vendor': 'vendor',
            'amount': 'amount',
            'checkNumber': 'check_number',
            'invoiceNumber': 'invoice_number',
            'invoiceDate': 'invoice_date',
            'dueDate': 'due_date',
            'scheduledPaymentDate': 'scheduled_payment_date',
            'statusDescription': 'status_description',
        };
        return columnMapping[column] || column;
    }

    loadPayables(
            orgId: number,
            tab: PayableFilterTab = 'unpaid',
            search: string = '',
            sortColumn: string = 'due_date',
            sortDirection: string = 'desc',
            page: number = 1,
            perPage: number = 25,
            filters?: SelectedPayableFilters,
    ): Observable<PayablesResponse> {
        this.loading = true;
        this.cancelInFlight$.next();
        const mappedSortColumn = this.mapSortColumn(sortColumn);
        return this.payablesService.getPayables(
            orgId,
            tab,
            search,
            mappedSortColumn,
            sortDirection,
            page,
            perPage,
            this.buildOptions(filters),
            this.buildHighlightOptions(),
        ).pipe(
            takeUntil(this.cancelInFlight$),
            catchError((err) => {
                return of({data: [], meta: {currentPage: 1, total: 0}}).pipe(tap(_ => console.error(err)));
            }),
            tap((payables: PayablesResponse) => {
                this.payableSubject$.next(payables.data);

                const total = (payables.meta?.total) ? payables.meta.total : 0;
                this.countSubject$.next(total);

                this.currentHighlightSubject$.next({
                    index: payables.highlightIndex,
                    meta: payables.meta,
                });
            }),
            finalize(() => this.loading = false),
            map((payables: PayablesResponse) => payables),
        );
    }

    connect(): Observable<Payable[]> {
        return this.payableSubject$.asObservable();
    }

    disconnect(): void {
        this.payableSubject$.complete();
        this.loadingSubject$.complete();
        this.countSubject$.complete();
    }

    highlight() {
        return this.currentHighlightSubject$.pipe(
            filter((res) => res?.index > -1),
            switchMap(() => this.scrollHighlightedIntoView(this.payableSubject$.getValue())),
        );
    }

    public buildOptions(filters: SelectedPayableFilters): PayableDatasourceOptions {
        const options: PayableDatasourceOptions = {};
        if (!filters) {
            return options;
        }

        if (filters.status && filters.status.length > 0 && filters.status[0] !== 'all') {
            options.status = filters.status;
        }

        if (filters.approvalStatus && filters.approvalStatus.length > 0 && filters.approvalStatus[0] !== 'all') {
            options.approvalStatus = filters.approvalStatus;
        }

        if (filters.payableApprover) {
            options.payableApprover = filters.payableApprover;
        }

        if (filters.fulfillment && filters.fulfillment !== 'all') {
            options.fulfillment = filters.fulfillment;
        }

        if (filters.vendor && filters.vendor !== 0) {
            options.vendor = filters.vendor;
        }

        if (filters.time !== 0) {
            options.year = filters.time;

            if (filters.time === -1) {
                if (filters.startTimeFrame && filters.endTimeFrame) {
                    options.startDate = filters.startTimeFrame.format('YYYY-MM-DD');
                    options.endDate = filters.endTimeFrame.format('YYYY-MM-DD');
                }
            }
        }

        if (filters.dueTime !== 0) {
            options.dueYear = filters.dueTime;

            if (filters.dueTime === -1) {
                if (filters.dueStartTimeFrame && filters.dueEndTimeFrame) {
                    options.dueStartDate = filters.dueStartTimeFrame.format('YYYY-MM-DD');
                    options.dueEndDate = filters.dueEndTimeFrame.format('YYYY-MM-DD');
                }
            }
        }

        if (filters.overdue) {
            options.overdue = true;
        }

        if (filters.deleted) {
            options.deleted = true;
        }

        // Always include hideCancelled - defaults to true if not explicitly set
        options.hideCancelled = filters.hideCancelled !== undefined ? filters.hideCancelled : true;

        return options;
    }

}

export class PayablesResponse
{
    data: Payable[];
    links: PaginationLinks;
    meta: PaginationMeta;

    highlightIndex: number;

    constructor(request: any) {
        this.data = (request.data && request.data.length) ? request.data.map((payable: any) => {
            return new Payable(payable);
        }) : [];
        this.links = new PaginationLinks(request);
        this.meta = new PaginationMeta(request.meta);

        this.highlightIndex = request?.highlightIndex ?? -1;
    }
}
